






























































































































































































































// vue
import {
    computed,
    defineComponent,
    onBeforeMount,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';

// store
import { useStore } from '@/store';

// hooks
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { useGeoRecordModify } from '@/vue3/hooks/geoRecordModifyHook';
import { useNotify } from '@/vue3/hooks/notifyHook';
import { usePrimeVue } from '@/vue3/hooks/primeVueHook';
import { useCursor } from '@/vue3/hooks/cursorHook';
import { useMeasureRecord } from '@/vue3/hooks/measureRecordHook';

// services
import { useService } from '@/vue3/api/services';

// models
import { GeoRecord, GeoRecordCategorySummary } from '@/vue3/api/models';

// primevue
import Button from 'primevue/button';
import Panel from 'primevue/panel';
import ProgressSpinner from 'primevue/progressspinner';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Dialog from 'primevue/dialog';
import { Portal } from '@linusborg/vue-simple-portal';

// turf
import * as turf from '@turf/turf';

// config
import Tools from '@/vue3/configs/drawToolsConfig';

// components
import GeoRecordEditor from '@/vue3/components/GeoRecordEditor.vue';
import UploadGeoJson from '@/vue3/components/geo-record-tools/UploadGeoJson.vue';
import FreehandRectangleTool from '@/vue3/components/geo-record-tools/FreehandRectangleTool.vue';
import DimensionRectangleTool from '@/vue3/components/geo-record-tools/DimensionRectangleTool.vue';
import ModifyGeometry from '@/vue3/components/geo-record-editor/ModifyGeometry.vue';
import Confirm from '@/vue3/components/Confirm.vue';
import GeoRecordEditorMobile from '@/vue3/components/GeoRecordEditorMobile.vue';

export default defineComponent({
    components: {
        Button,
        Panel,
        ProgressSpinner,
        TabView,
        TabPanel,
        FreehandRectangleTool,
        DimensionRectangleTool,
        GeoRecordEditor,
        UploadGeoJson,
        ModifyGeometry,
        Dialog,
        Confirm,
        GeoRecordEditorMobile,
        Portal,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
        setMapboxDrawSnap: {
            type: Function,
            default: () => {},
        },
        updateUtilityDrawerState: {
            type: Function,
            default: () => {},
        },
        isGpsActive: {
            type: Boolean,
            default: false,
        },
        setActiveTool: {
            type: Function,
            default: () => {},
        },
        activeCreateToolName: {
            type: String,
            default: '',
        },
        setActiveCreateToolName: {
            type: Function,
            default: () => {},
        },
        layerTree: {
            type: Object,
            default: () => {},
        },
        utilityDrawer: {
            type: Object,
            default: null,
        },
    },
    setup(props: any, vm: any) {
        // model
        const newGeoRecord = ref({} as GeoRecord);
        const categorySummaries = ref<GeoRecordCategorySummary[]>([]);
        const createdGeoRecord = ref({} as GeoRecord);

        // hooks
        const {
            getters,
            state: { isMobile, accountPermissions },
        } = useStore();
        const {
            addGeoRecord,
            refreshGeoRecord,
            removeRectangleGeoRecordSource,
            initCreateGeoRecordSource,
            removeCreateGeoRecordSource,
            initCursorSource,
            removeCursorSource,
            removeCreatedGeoRecordExtrusion,
            refreshGeoRecordExtrusion,
            removeSelectedGeoRecord,
            removeGeoRecordExtrusion,
            removeGeoRecord,
            initSelectedGeoRecord,
        } = useGeoRecord();
        const { successToast, errorToast } = useNotify();
        const { updateModifiedGeoRecord } = useGeoRecordModify();
        const { tabHandler } = usePrimeVue();
        const { handleMoveCursor } = useCursor();
        const { calculateTotalMeasurement } = useMeasureRecord();

        // services
        const {
            createGeoRecord,
            getSrid,
            getGeoRecordCategorySummaryByRole,
            categoriesToArray,
            updateGeoRecord,
            getGeoRecord,
            getRole,
            getGeoRecordCategoryByRole,
            createArchiveGeoRecord,
        } = useService();

        // refs
        const editing = ref(false);
        const isRectangleLayerVisible = ref(false);
        const isRectangleToolSelected = ref(false);
        const isLoading = ref(false);
        const isUserAdmin = ref(false);
        const isModifying = ref(false);
        const isSnapActivated = ref(false);
        const activeToolMode = ref();
        const isMagneticOptionVisible = ref(false);
        const isModificationModeActive = ref(false);
        const isGeoRecordCreated = ref(false);
        const isGeomertryValid = ref(true);
        const isArchiveRecord = ref<boolean>(false);

        const activeTool = ref('');
        const activeToolName = ref('');
        const activeToolDesc = ref('');

        const courseSrid = ref(0);

        const newCategoryDefaultId = ref();
        const role = ref();
        const store = useStore();
        const freehandComponentRef = ref<
            InstanceType<typeof FreehandRectangleTool>
        >({} as InstanceType<typeof FreehandRectangleTool>);
        const dimensionComponentRef = ref();
        const selectButtonOption = ref({ name: '', value: '' });

        // computed
        const isEditing = computed(() => {
            return editing.value;
        });

        // methods
        const setSrid = async () => {
            courseSrid.value = await getSrid(getters.currentCourseSchema.id);
        };

        const moveCursor = ({ lngLat }): void => {
            handleMoveCursor(
                props.mapbox,
                isSnapActivated.value,
                activeToolName.value,
                lngLat,
            );
        };

        const activateCursor = () => {
            if (props.mapbox.map.getSource('create-cursor-point')) return;

            initCursorSource(props.mapbox.map);
            props.mapbox.map.on('mousemove', moveCursor);
        };

        const tabChange = (tab: any): void => {
            const resetFreeHand = (): void => {
                freehandComponentRef.value.resetFreehandRectangleValue();
                isRectangleToolSelected.value = true;
            };

            const activateFreeHand = (): void => {
                freehandComponentRef.value.activateFreeDrawMode();
            };

            tabHandler(tab.index, resetFreeHand, activateFreeHand);
        };

        const toggleModifyToolVisibility = (): void => {
            isModificationModeActive.value = true;
            isGeoRecordCreated.value = false;
            unmountTool();
        };

        const assignValueToGeoRecord = (geom: any): void => {
            newGeoRecord.value.accountId = getters.account.id;
            newGeoRecord.value.courseSchemaId = getters.currentCourseSchema.id;
            newGeoRecord.value.organizationId = getters.currentOrganization.id;
            newGeoRecord.value.isFill = geom.type === 'Polygon' ? true : false;
            newGeoRecord.value.geometry = geom;
            newGeoRecord.value.geoRecordCategoryId = newCategoryDefaultId.value;
            newGeoRecord.value.archiveEventId = 0;
        };

        const checkDefaultCategoryPermission = () => {
            for (vm of role.value.roleGeoRecordCategoryPermissionsLink) {
                if (vm.id === newCategoryDefaultId.value) return vm.canEdit;
            }
        };

        const createNewGeoRecord = async (): Promise<void> => {
            const data = props.mapbox.draw.getAll();
            const isGeomValid = data.features[0].properties.isGeometryValid;
            data.features[0].properties.isVisible = true;

            assignValueToGeoRecord(data.features[0].geometry);

            if (isGeomValid) {
                isGeomertryValid.value = isGeomValid;
            }

            if (props.mapbox.map.getSource('creating-geo-record')) {
                removeCreateGeoRecordSource(props.mapbox.map);
            }

            if (props.mapbox.map.getLayer('rectangle-geo-record-polygon')) {
                removeRectangleGeoRecordSource(props.mapbox.map);
            }
            newGeoRecord.value.isPrivate = false;
            await saveGeoRecord(
                newGeoRecord.value as GeoRecord,
                data.features[0].geometry.type,
            );
            initSelectedGeoRecord(props.mapbox.map, newGeoRecord.value);

            if (getters.isMobile) {
                // props.utilityDrawer.isUtilityOpen = false;
                props.updateUtilityDrawerState(false);

                const feature = newGeoRecord.value;
                let center;

                if (feature.geometry.type === 'Point') {
                    center = feature.geometry.coordinates;
                } else {
                    const centroid = turf.centroid(feature.geometry);
                    center = centroid.geometry?.coordinates;
                }

                const offset = -0.003;

                props.mapbox.map.flyTo({
                    center: [center[0], center[1] + offset],
                    zoom: 15,
                });
            }

            toolClick(activeTool.value);
        };

        const clearDownSave = async (id: number) => {
            editing.value = false;
            isModificationModeActive.value = false;

            let georecordArry = [id];

            await props.layerTree.refreshGeoRecordTreeAndState(
                georecordArry,
                true,
            );
            //props.layerTree.refreshGeoRecordState();
            isLoading.value = false;
        };

        const saveGeoRecord = async (
            geoRecord: GeoRecord,
            geometryType: string,
        ): Promise<void> => {
            if (!checkDefaultCategoryPermission()) {
                errorToast('Permission required!');
                return;
            }

            isLoading.value = true;
            isGeoRecordCreated.value = false;

            if (!isGeomertryValid.value) {
                clearDownSave(0);
                return;
            }

            try {
                createdGeoRecord.value = await createGeoRecord(geoRecord);
                createdGeoRecord.value = await calculateTotalMeasurement(
                    createdGeoRecord.value,
                    geometryType,
                );
            } catch (error) {
                console.log(error);
                toolClick(activeTool.value);
                clearGeoRecord();
                editing.value = false;
                isLoading.value = false;
                return;
            }

            updateGeoRecordLayer(createdGeoRecord.value as GeoRecord);
            newGeoRecord.value = createdGeoRecord.value;
            initCreateGeoRecordSource(props.mapbox.map, newGeoRecord.value);

            isGeoRecordCreated.value = true;
            clearDownSave(createdGeoRecord.value.id);
        };

        const updateGeoRecordLayer = (geoRecord: any) => {
            successToast(
                `Geo Record: '${geoRecord.title}' saved.`,
                getters.isMobile ? 'fullscreen' : 'default',
            );

            props.mapbox.draw.deleteAll();
            addGeoRecord(props.mapbox.map, geoRecord);
        };

        const getNewGeometry = (): void => {
            activateCursor();

            props.mapbox.map.on('draw.create', createNewGeoRecord);

            isRectangleToolSelected.value = false;
        };

        const toggleSnappingEffect = (): void => {
            isSnapActivated.value = !isSnapActivated.value;
            // props.mapbox.draw.options.snap = isSnapActivated.value;
            props.setMapboxDrawSnap(isSnapActivated.value);
            if (
                activeToolMode.value == 'draw_line_string' ||
                activeToolMode.value == 'SnapLineStringMode' ||
                activeToolMode.value == 'DrawLineStringMode'
            ) {
                activeToolMode.value = isSnapActivated.value
                    ? 'SnapLineStringMode'
                    : 'DrawLineStringMode';
            } else if (
                activeToolMode.value == 'draw_polygon' ||
                activeToolMode.value == 'SnapPolygonMode' ||
                activeToolMode.value == 'DrawPolygonMode'
            ) {
                activeToolMode.value = isSnapActivated.value
                    ? 'SnapPolygonMode'
                    : 'DrawPolygonMode';
            } else {
                //for other tools, activeToolMode.value is not changed
            }

            props.mapbox.draw.changeMode(activeToolMode.value);
        };

        const toolClick = (tool): void => {
            activeTool.value = tool;
            activeToolName.value = tool.name;
            activeToolDesc.value = tool.description;
            activeToolMode.value = tool.mode;
            selectButtonOption.value = { name: 'Yard', value: 'y' };

            if (
                activeToolName.value === 'Line Tool' ||
                activeToolName.value === 'Polygon Tool'
            ) {
                isMagneticOptionVisible.value = true;
            } else {
                isMagneticOptionVisible.value = false;
                isSnapActivated.value = false;
            }

            props.mapbox.draw.deleteAll();

            if (
                activeToolMode.value == 'draw_line_string' ||
                activeToolMode.value == 'SnapLineStringMode' ||
                activeToolMode.value == 'DrawLineStringMode'
            ) {
                activeToolMode.value = isSnapActivated.value
                    ? 'SnapLineStringMode'
                    : 'DrawLineStringMode';
            } else if (
                activeToolMode.value == 'draw_polygon' ||
                activeToolMode.value == 'SnapPolygonMode' ||
                activeToolMode.value == 'DrawPolygonMode'
            ) {
                activeToolMode.value = isSnapActivated.value
                    ? 'SnapPolygonMode'
                    : 'DrawPolygonMode';
            } else {
                //for other tools, activeToolMode.value is not changed
            }

            if (store.state.isGPSCreateModeOn) {
                activeToolMode.value =
                    activeToolMode.value == 'draw_point'
                        ? 'DrawPointGPS'
                        : activeToolMode.value == 'draw_line_string'
                        ? 'DrawLineGPS'
                        : activeToolMode.value == 'draw_polygon'
                        ? 'DrawPolygonGPS'
                        : activeToolMode.value;
            }

            props.mapbox.draw.changeMode(activeToolMode.value);

            if (tool.name === 'Rectangle Tool') {
                if (!isEditing.value) {
                    isRectangleToolSelected.value = true;
                }
            } else {
                getNewGeometry();
            }
        };

        const setIsModifying = (value: boolean): void => {
            if (value) {
                isModifying.value = value;
                unmountTool();
            } else {
                isModifying.value = value;
                toolClick(activeTool.value);
                refreshGeoRecord(props.mapbox.map, newGeoRecord.value, true);
                removeSelectedGeoRecord(props.mapbox.map, newGeoRecord.value);
            }
        };

        const clearGeoRecord = (): void => {
            setIsModifying(false);
            isRectangleLayerVisible.value = false;
            editing.value = false;
            isModificationModeActive.value = false;
            isGeoRecordCreated.value = false;
            props.mapbox.draw.deleteAll();
            newGeoRecord.value = {} as GeoRecord;

            removeCreatedGeoRecordExtrusion(props.mapbox.map);
            toolClick(activeTool.value);

            if (props.mapbox.map.getSource('creating-geo-record')) {
                removeCreateGeoRecordSource(props.mapbox.map);
            }
        };

        const unmountTool = (): void => {
            props.mapbox.draw.changeMode('simple_select');
            props.mapbox.map.off('draw.create', createNewGeoRecord);

            removeCursorSource(props.mapbox.map);
            props.mapbox.draw.deleteAll();
            removeCreateGeoRecordSource(props.mapbox.map);
        };

        const moveGeoRecordToArchive = async (geoRecord: any) => {
            await createArchiveGeoRecord(geoRecord);

            successToast(
                `Geo Record: '${geoRecord.title}' successfully archived.`,
                getters.isMobile ? 'fullscreen' : 'default',
            );

            isModificationModeActive.value = false;
            isGeoRecordCreated.value = false;

            removeSelectedGeoRecord(props.mapbox.map, geoRecord);
            removeGeoRecordExtrusion(props.mapbox.map, geoRecord);
            removeGeoRecord(props.mapbox.map, geoRecord);
        };

        const updateNewGeoRecord = async (): Promise<void> => {
            isLoading.value = true;

            newGeoRecord.value = updateModifiedGeoRecord(
                props.mapbox.draw,
                newGeoRecord.value,
                categorySummaries.value,
            );

            if (!newGeoRecord.value.archiveEventId) {
                await updateAndRefreshGeoRecord();
            }

            if (newGeoRecord.value.archiveEventId && isArchiveRecord.value) {
                await confirmMoveRecordToArchive();
            }

            await props.layerTree.refreshGeoRecordTree();

            isLoading.value = false;
        };

        const updateAndRefreshGeoRecord = async () => {
            let geoRecordId = newGeoRecord.value.id;

            try {
                const res = await updateGeoRecord(newGeoRecord.value, true);
                if (res.status == 200 || res.status == 201) {
                    successToast(
                        'Successful!',
                        getters.isMobile ? 'fullscreen' : 'default',
                    );
                } else {
                    errorToast('Error!');
                }
            } catch (error) {
                console.log(error);
                clearGeoRecord();
                return;
            }
            await updateGeoRecord(newGeoRecord.value, true);

            let updatedGeoRecord = await getGeoRecord(geoRecordId);

            newGeoRecord.value = updatedGeoRecord;
            props.mapbox.draw.deleteAll();

            refreshGeoRecord(props.mapbox.map, newGeoRecord.value, true);
            refreshGeoRecordExtrusion(props.mapbox.map, newGeoRecord.value);
            clearGeoRecord();
        };

        const confirmMoveRecordToArchive = async () => {
            try {
                await moveGeoRecordToArchive(newGeoRecord.value);
                await props.layerTree.refershArchiveGeoRecordLayerTree();
                await props.layerTree.fetchArchiveEvents();
                isArchiveRecord.value = false;
            } catch (error) {
                console.log(error);
                clearGeoRecord();
                return;
            }
            let georecordArry = [props.value.id];

            await props.layerTree.refreshGeoRecordTreeAndState(
                georecordArry,
                false,
            );
            props.layerTree.refreshGeoRecordState();
        };

        const toggleIsArchive = (value: boolean) => {
            isArchiveRecord.value = value;
        };

        const declineArchiveMove = (): void => {
            isArchiveRecord.value = false;
        };

        const setRoleValue = async (): Promise<void> => {
            isLoading.value = true;
            role.value = await getRole(getters.account.roleId);
            isLoading.value = false;
        };

        const assignDefaultCategoryId = async (): Promise<void> => {
            (await getGeoRecordCategoryByRole()).forEach((cat) => {
                if (cat.name === 'No Category') {
                    newCategoryDefaultId.value = cat.id;
                    return;
                }
            });
        };

        const updateGeoRecordExternal = (newValue: any) => {
            newGeoRecord.value = newValue;
            console.log('updateGeoRecordExternal create', newGeoRecord.value);
        };

        // lifecycle
        onBeforeMount(async () => {
            isLoading.value = true;

            if (accountPermissions?.indexOf('canEnableAdministrator') !== -1) {
                isUserAdmin.value = true;
            }

            await setRoleValue();
            await assignDefaultCategoryId();

            categorySummaries.value = categoriesToArray(
                await getGeoRecordCategorySummaryByRole(),
            );

            isLoading.value = false;
        });

        onMounted(async () => {
            toolClick(Tools[0]);
            setSrid();
        });

        onUnmounted(() => {
            unmountTool();
        });

        return {
            Tools,
            activeToolName,
            activeToolDesc,
            isEditing,
            newGeoRecord,
            updateGeoRecordExternal,
            isGeoRecordCreated,
            isUserAdmin,
            categorySummaries,
            isLoading,
            isModifying,
            isRectangleToolSelected,
            freehandComponentRef,
            dimensionComponentRef,
            toolClick,
            clearGeoRecord,
            setIsModifying,
            updateNewGeoRecord,
            tabChange,
            createNewGeoRecord,
            saveGeoRecord,
            activateCursor,
            updateGeoRecordLayer,
            isSnapActivated,
            isMagneticOptionVisible,
            toggleSnappingEffect,
            isModificationModeActive,
            toggleModifyToolVisibility,
            isMobile,
            newCategoryDefaultId,
            role,
            isArchiveRecord,
            toggleIsArchive,
            declineArchiveMove,
            getters,
        };
    },
});
